import { type ReactNode, useCallback } from 'react'

import { GallerySingleItem } from '@/features/listing/components/gallery-single-item/GallerySingleItem'
import { RichCardImagePanel } from '@/features/listing/components/rich-card/rich-card-media/RichCardImagePanel'
import { RichCardCarouselContainer } from '@/features/listing/components/rich-card/styled'
import { RICH_CARD_CAROUSEL_MAX_IMAGES } from '@/features/listing/constants/richCard'
import { type GenericListing } from '@/features/listing/types/listings'

export const RichCardCarousel = ({
  listing,
  titleOverride,
}: {
  listing: GenericListing
  titleOverride?: ReactNode | string
}) => {
  const { imageUrls } = listing
  const imageCount = 'imageCount' in listing ? listing.imageCount : undefined

  const getImageSlides = useCallback(
    (listing: GenericListing) => {
      const { imageUrls, id } = listing
      const flags = 'flags' in listing ? listing.flags : undefined
      const showcase = flags?.showcase ?? false
      let slideImages = showcase ? imageUrls : imageUrls.slice(0, RICH_CARD_CAROUSEL_MAX_IMAGES)

      /** If theres is only one image, duplicate it for the overlay slide */
      if (slideImages.length === 1) {
        slideImages = [...slideImages, slideImages[0]]
      }

      /** If theres are no images, show a fallback image and a blank overlay slide */
      if (slideImages.length === 0) {
        return [
          <RichCardImagePanel
            key={`${id}-fallback-image`}
            index={0}
            imageUrl={null}
            listing={listing}
          />,
          <RichCardImagePanel
            key={`${id}-overlay`}
            index={1}
            imageUrl="blank"
            listing={listing}
            showDetailsOverlay={true}
            titleOverride={titleOverride}
          />,
        ]
      }

      return slideImages.map((image, index) => {
        const showDetailsOverlay = index === slideImages.length - 1

        return (
          <RichCardImagePanel
            key={`${id}-${index}`}
            index={index}
            imageUrl={image}
            listing={listing}
            showDetailsOverlay={showDetailsOverlay}
            titleOverride={titleOverride}
          />
        )
      })
    },
    [titleOverride]
  )

  const slides = getImageSlides(listing)

  return (
    <RichCardCarouselContainer data-testid="rich-card-carousel">
      <GallerySingleItem
        name={`gallery-${listing.id}`}
        slides={slides}
        overrideSlideCount={imageCount ?? imageUrls.length}
        showArrows
        showDots
      />
    </RichCardCarouselContainer>
  )
}
